import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Check, X, Trash2 } from "lucide-react";
import { Panel, Table, Field, TextInput, TextArea, Select, Btn, Modal, EmptyState } from "./ui";
import { useI18n } from "../i18n";
import { CL } from "../api";
import { formatApiError } from "../auth";

const SECTORS = ["Çimento", "Demir-Çelik", "Alüminyum", "Gübre", "Elektrik", "Hidrojen"];
const STATUS_CLS = { pending: "bg-[#f4f6ee] text-[#6b7268]", submitted: "bg-[#fff4e0] text-[#b4541a]", approved: "bg-[#eef6ef] text-[#14512f]", rejected: "bg-red-50 text-red-700" };

export const StatusPill = ({ status }) => {
  const { t } = useI18n();
  return <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_CLS[status]}`} data-testid={`status-${status}`}>{t(`st_${status}`)}</span>;
};

const NewRequestModal = ({ open, onClose, onCreated, year, quarter, countries }) => {
  const { t } = useI18n();
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ supplier_email: "", cn_code: "", product: "", sector: "Demir-Çelik", country: "Türkiye", quantity: "", note: "" }); }, [open]);
  const up = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    try {
      await CL.supReqCreate({ ...f, quantity: Number(f.quantity || 0), year: Math.max(year, 2026), quarter });
      toast.success(t("requestSent")); onCreated(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  return (
    <Modal open={open} onClose={onClose} title={`${t("supReqNew")} — ${Math.max(year, 2026)} ${quarter}`}
      footer={<><Btn variant="outline" onClick={onClose}>{t("cancel")}</Btn><Btn onClick={save} disabled={!f.supplier_email} data-testid="supreq-save"><Send className="h-4 w-4" />{t("supReqNew")}</Btn></>}>
      <p className="text-xs text-[#6b7268]">{t("supReqHint")}</p>
      <Field label={t("supplierEmail")}><TextInput type="email" data-testid="supreq-email" value={f.supplier_email || ""} onChange={up("supplier_email")} /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label={t("cnCode")}><TextInput data-testid="supreq-cn" value={f.cn_code || ""} onChange={up("cn_code")} /></Field>
        <Field label={t("sector")}><Select data-testid="supreq-sector" value={f.sector} onChange={up("sector")}>{SECTORS.map((s) => <option key={s} value={s}>{t(`sec_${s}`)}</option>)}</Select></Field>
      </div>
      <Field label={t("product")}><TextInput data-testid="supreq-product" value={f.product || ""} onChange={up("product")} /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label={t("origin")}><Select data-testid="supreq-country" value={f.country} onChange={up("country")}>{countries.map((c) => <option key={c} value={c}>{c}</option>)}</Select></Field>
        <Field label={t("quantityT")}><TextInput type="number" data-testid="supreq-quantity" value={f.quantity || ""} onChange={up("quantity")} /></Field>
      </div>
      <Field label={t("noteLbl")}><TextArea data-testid="supreq-note" value={f.note || ""} onChange={up("note")} /></Field>
    </Modal>
  );
};

export const CbamSupplierRequests = ({ year, quarter, countries, onApproved }) => {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { CL.supReqList().then(setRows).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); load(); onApproved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  return (
    <Panel className="mt-6" data-testid="supreq-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-lg text-[#16321f]">{t("supReqTitle")}</h3>
        <Btn onClick={() => setOpen(true)} data-testid="supreq-new"><Send className="h-4 w-4" />{t("supReqNew")}</Btn>
      </div>
      {rows.length === 0 ? <EmptyState text={t("noData")} /> : (
        <Table head={[{ label: t("supplierEmail") }, { label: t("product") }, { label: t("period") }, { label: t("installation") }, { label: t("status") }, { label: t("actions"), right: true }]}>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`supreq-row-${r.id}`}>
              <td className="px-4 py-3 text-[#1b2420]">{r.supplier_email}</td>
              <td className="px-4 py-3 text-[#4b544c]">{r.product || r.cn_code || "—"}<div className="text-xs text-[#8a8f83]">{r.quantity} t · {r.country}</div></td>
              <td className="px-4 py-3 text-[#4b544c]">{r.year} {r.quarter}</td>
              <td className="px-4 py-3 text-xs text-[#4b544c]">{r.submission ? <>{r.submission.installation_name}<div>{t("directIntensity")}: {r.submission.direct} · {t("indirectIntensity")}: {r.submission.indirect}</div>{r.submission.verifier && <div>{t("verifier")}: {r.submission.verifier}</div>}</> : "—"}</td>
              <td className="px-4 py-3"><StatusPill status={r.status} /></td>
              <td className="px-4 py-3">
                <div className="flex justify-end gap-1">
                  {r.status === "submitted" && <>
                    <Btn variant="outline" onClick={() => act(() => CL.supReqApprove(r.id), t("approvedToast"))} data-testid={`supreq-approve-${r.id}`}><Check className="h-4 w-4" />{t("approve")}</Btn>
                    <Btn variant="outline" onClick={() => act(() => CL.supReqReject(r.id), t("st_rejected"))} data-testid={`supreq-reject-${r.id}`}><X className="h-4 w-4" /></Btn>
                  </>}
                  <Btn variant="outline" onClick={() => window.confirm(t("confirmDelete")) && act(() => CL.supReqDelete(r.id))} data-testid={`supreq-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Btn>
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
      <NewRequestModal open={open} onClose={() => setOpen(false)} onCreated={load} year={year} quarter={quarter} countries={countries} />
    </Panel>
  );
};
